<template>
    <div
        class="bo-price-display"
        :class="[`bo-price-display--${size}`, `bo-price-display--align-${align}`]"
    >
        <!-- Row 1: the price the customer pays, its tax tag and the points earned -->
        <div class="bo-price-display__main">
            <span
                class="bo-price-display__price"
                :class="{
                    'bo-price-display__price--discounted': hasDiscount,
                    'bo-price-display__price--range': hasRange,
                }"
            >
                <template v-if="prefix">{{ prefix }}&nbsp;</template>{{ formatAmount(price)
                }}<template v-if="hasRange"> - {{ formatAmount(maxPrice) }}</template>
            </span>
            <span
                v-if="taxLabel"
                class="bo-price-display__tax"
                >{{ taxLabel }}</span
            >
            <bo-point
                v-if="showPoint"
                class="bo-price-display__point"
                :value="point"
            ></bo-point>
        </div>

        <!-- Row 2: the original price struck through, then every discount level -->
        <div
            v-if="hasDiscount"
            class="bo-price-display__discount-row"
        >
            <span class="bo-price-display__original">{{ formatAmount(originalPrice) }}</span>
            <bo-line-discount
                v-if="discountSteps.length > 0"
                class="bo-price-display__discount"
                show-all
                :steps="discountSteps"
            ></bo-line-discount>
        </div>
    </div>
</template>

<script>
import BoPoint from "js/frontend/components/bo-point.vue";
import BoLineDiscount from "js/frontend/components/bo-line-discount.vue";

// Product price block for the catalog, quick add popup and product page:
// discounted price + tax tag + points on top, original price + discount levels below.
export default {
    components: { BoPoint, BoLineDiscount },

    props: {
        price: {
            type: [Number, String],
            default: 0,
        },
        // Upper end of a price range ("RM 1.00 - 2.00"); null for a single price.
        maxPrice: {
            type: [Number, String],
            default: null,
        },
        // e.g. "From" for bundles
        prefix: {
            type: String,
            default: null,
        },
        // Price before discount; the discount row only shows when it is above price.
        originalPrice: {
            type: [Number, String],
            default: null,
        },
        // Pre-formatted discount levels, e.g. ["-RM 5.00", "-2.50%"]
        discountSteps: {
            type: Array,
            default: () => [],
        },
        currencySymbol: {
            type: String,
            default: () => window.bo?.currencySymbol ?? "RM",
        },
        taxLabel: {
            type: String,
            default: null,
        },
        point: {
            type: Number,
            default: 0,
        },
        showPoint: {
            type: Boolean,
            default: false,
        },
        // large: product page / quick add popup, medium: catalog list, small: bundle component list
        size: {
            type: String,
            default: "large",
        },
        // start | end (right-aligned price columns)
        align: {
            type: String,
            default: "start",
        },
    },

    computed: {
        hasRange() {
            return this.maxPrice != null && Number(this.maxPrice) !== Number(this.price);
        },
        hasDiscount() {
            return !this.hasRange && this.originalPrice != null && Number(this.originalPrice) > Number(this.price);
        },
    },

    methods: {
        // Line prices always display at 2dp (see line-discount-label.js), with thousand separators.
        formatAmount(value) {
            const amount = (Number(value) || 0).toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
            // Non-breaking space keeps "RM 98.50" together when a price range wraps at its dash.
            return `${this.currencySymbol}\u00A0${amount}`;
        },
    },
};
</script>

<style lang="scss" scoped>
@import "../../../sass/variables";

.bo-price-display {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;

    --price-size: 24px;
    --original-size: 14px;
    --tag-size: 12px;

    &--medium {
        --price-size: 16px;
        --original-size: 12px;
        --tag-size: 10px;
    }

    &--small {
        --price-size: 14px;
        --original-size: 12px;
        --tag-size: 10px;
    }

    &--align-end {
        align-items: flex-end;

        .bo-price-display__main,
        .bo-price-display__discount-row {
            justify-content: flex-end;
        }
    }
}

.bo-price-display__main,
.bo-price-display__discount-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px 8px;
}

.bo-price-display__price {
    font-size: var(--price-size);
    font-weight: 600;
    line-height: 1.2;
    color: $color-darkfontbase;
    white-space: nowrap;

    &--discounted {
        color: $color-danger;
    }

    // A range can be wider than the card: it may wrap at the dash, never inside an amount.
    &--range {
        white-space: normal;
    }
}

.bo-price-display__tax {
    border: 1px solid $color-lightfontbase;
    border-radius: 2px;
    padding: 0 4px;
    font-size: var(--tag-size);
    font-weight: 500;
    color: $color-fontbase;
    white-space: nowrap;
}

// The global .bo-point is full width; here it is an inline badge next to the price.
.bo-price-display__point {
    width: auto;
    min-height: 0;
    padding: 2px 6px;
    border-radius: 4px;
    background-color: rgba(255, 178, 0, 0.1);
    font-size: var(--tag-size);
    white-space: nowrap;
}

.bo-price-display__original {
    font-size: var(--original-size);
    font-weight: 400;
    color: $color-lightfontbase;
    text-decoration: line-through;
    white-space: nowrap;
}

.bo-price-display__discount {
    padding: 2px 6px;
    border-radius: 4px;
    background-color: $color-danger-background;
    color: $color-danger;
    font-size: var(--original-size);
    font-weight: 600;
}

// The discount levels are the same size as the price before discount. Page styles that size every
// `span` (catalog, product page, cart) would otherwise reach the spans inside these two and pull them apart.
.bo-price-display__original,
.bo-price-display__discount,
.bo-price-display__discount ::v-deep span {
    font-size: var(--original-size) !important;
    line-height: 1.4;
}
</style>
